import { FC, useContext, useEffect } from 'react';
import { RadioButton, RadioButtonGroup } from '@carbon/react';
import { useAtomValue } from 'jotai';
import { useStore } from '@tanstack/react-store';

import { useWizardStep } from 'components/full-page-wizard';
import { DatePicker } from 'components/date-picker';
import { getDateString } from 'utils/dates';

import { AfeWizardCtx } from '../context';
import { afeEnumsLoadableAtom } from '../atoms';
import type { EnumOption } from '../types';
import styles from './afe-type-step.module.scss';

/**
 * Step 1 — AFE Type selection. Displays a heading, subtitle, and 7
 * horizontal radio buttons populated from enums. Bridges validation
 * to the wizard via useWizardStep.
 * @return {JSX.Element} The AFE type step content
 */
const AfeTypeStep: FC = () => {
  const { form } = useContext(AfeWizardCtx)!;
  const { setNextEnabled } = useWizardStep();
  const enumsLoadable = useAtomValue(afeEnumsLoadableAtom);
  const options: EnumOption[] =
    enumsLoadable.state === 'hasData' ? enumsLoadable.data.afeType : [];

  const afeType = useStore(form.store, (s) => s.values.afeType);

  useEffect(() => {
    setNextEnabled(afeType !== null);
  }, [afeType, setNextEnabled]);

  return (
    <div>
      <h2 className={styles.heading}>Select AFE Type*</h2>
      <p className={styles.subtitle}>
        Select your desired AFE Type and continue
      </p>
      <form.Field name="afeType">
        {(field) => (
          <RadioButtonGroup
            name="afeType"
            legendText="AFE Type"
            valueSelected={field.state.value ?? ''}
            onChange={(value: string | number | undefined) =>
              field.handleChange(value as string)
            }
            orientation="horizontal"
          >
            {options.map((opt) => (
              <RadioButton
                key={opt.value}
                value={opt.value}
                labelText={opt.label}
              />
            ))}
          </RadioButtonGroup>
        )}
      </form.Field>
      <div className={styles.afeDate}>
        <form.Field name="afeDate">
          {(field) => (
            <DatePicker
              id="afe-date"
              labelText="AFE Date"
              value={field.state.value ?? undefined}
              onChange={(dates) =>
                field.handleChange(dates[0] ? getDateString(dates[0]) : null)
              }
              appendTo={document.body}
            />
          )}
        </form.Field>
      </div>
    </div>
  );
};

export { AfeTypeStep };
